import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Bot, Play, Pause, Trash2, Brain, TrendingUp, Calculator, Activity, Eye, GitBranch } from 'lucide-react'
import { botsApi, strategiesApi } from '../lib/api'
import { formatCurrency } from '../lib/utils'

export function Bots() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showFormulasModal, setShowFormulasModal] = useState(false)
  const [newBot, setNewBot] = useState({
    name: '',
    mode: 'COPY_TRADE',
    strategyId: '',
    maxPositionSize: 100,
    kellyFraction: 0.25,
    minSharpeRatio: 1.5,
    minWinRate: 0.55,
    maxDailyLoss: 1000,
    maxOpenPositions: 5,
    categoryFilter: [],
  })

  const { data: bots, isLoading } = useQuery({
    queryKey: ['bots'],
    queryFn: () => botsApi.getAll().then(r => r.data),
  })

  const { data: strategies } = useQuery({
    queryKey: ['strategies'],
    queryFn: () => strategiesApi.getAll().then(r => r.data),
    enabled: showCreateModal,
  })

  const toggleMutation = useMutation({
    mutationFn: (id: string) => botsApi.toggle(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['bots'] }),
  })

  const createMutation = useMutation({
    mutationFn: (data: any) => botsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bots'] })
      setShowCreateModal(false)
      setNewBot({ 
        name: '', 
        mode: 'COPY_TRADE', 
        strategyId: '',
        maxPositionSize: 100, 
        kellyFraction: 0.25,
        minSharpeRatio: 1.5,
        minWinRate: 0.55,
        maxDailyLoss: 1000,
        maxOpenPositions: 5,
        categoryFilter: [],
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => botsApi.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['bots'] }),
  })

  if (isLoading) {
    return <div className="text-center py-12">Loading bots...</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Trading Bots</h1>
          <p className="mt-1 text-gray-500">AI-driven automated trading with Kelly Criterion, EV, and Sharpe Ratio</p>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowFormulasModal(true)}
            className="flex items-center px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            <Calculator className="w-4 h-4 mr-2" />
            The 3 Formulas
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Bot
          </button>
        </div>
      </div>

      {/* AI-Driven Explainer Card */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-start">
          <Brain className="w-8 h-8 mr-4 flex-shrink-0" />
          <div>
            <h2 className="text-xl font-bold mb-2">AI-Driven Trading Mode</h2>
            <p className="text-blue-100 mb-4">
              Our AI analyzes whale movements, calculates Expected Value, sizes positions using Kelly Criterion, 
              and filters by Sharpe Ratio — just like the viral post described.
            </p>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="bg-white/10 rounded-lg p-3">
                <TrendingUp className="w-5 h-5 mb-2" />
                <p className="font-semibold">EV {'>'} 0</p>
                <p className="text-blue-200">Only positive expected value trades</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <Calculator className="w-5 h-5 mb-2" />
                <p className="font-semibold">Kelly Criterion</p>
                <p className="text-blue-200">Quarter-Kelly position sizing</p>
              </div>
              <div className="bg-white/10 rounded-lg p-3">
                <Activity className="w-5 h-5 mb-2" />
                <p className="font-semibold">Sharpe Ratio</p>
                <p className="text-blue-200">Risk-adjusted returns filter</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bots Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {bots?.map((bot: any) => (
          <div
            key={bot.id}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  bot.isActive ? 'bg-green-100' : 'bg-gray-100'
                }`}>
                  {bot.mode === 'AI_DRIVEN' ? (
                    <Brain className={`w-5 h-5 ${bot.isActive ? 'text-green-600' : 'text-gray-600'}`} />
                  ) : (
                    <Bot className={`w-5 h-5 ${bot.isActive ? 'text-green-600' : 'text-gray-600'}`} />
                  )}
                </div>
                <div className="ml-3">
                  <h3 className="font-semibold text-gray-900">{bot.name}</h3>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      bot.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {bot.isActive ? 'Active' : 'Paused'}
                    </span>
                    <span className="text-xs text-gray-500">
                      {bot.mode.replace('_', ' ')}
                    </span>
                    {bot.strategy && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                        <GitBranch className="w-3 h-3 mr-1" />
                        {bot.strategy.name}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => navigate(`/bots/${bot.id}`)}
                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title="View Details"
                >
                  <Eye className="w-4 h-4" />
                </button>
                <button
                  onClick={() => toggleMutation.mutate(bot.id)}
                  className="p-2 text-gray-400 hover:text-gray-600"
                >
                  {bot.isActive ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => deleteMutation.mutate(bot.id)}
                  className="p-2 text-gray-400 hover:text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="mt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Max Position</span>
                <span className="font-medium">{formatCurrency(bot.maxPositionSize)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Kelly Fraction</span>
                <span className="font-medium">{(bot.kellyFraction * 100).toFixed(0)}%</span>
              </div>
              {bot.mode === 'AI_DRIVEN' && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Min Sharpe</span>
                    <span className="font-medium">{bot.minSharpeRatio}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Daily Loss Limit</span>
                    <span className="font-medium">{formatCurrency(bot.maxDailyLoss)}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Executions</span>
                <span className="font-medium">{bot._count?.executions || 0}</span>
              </div>
            </div>

            {/* View Details Link */}
            <button
              onClick={() => navigate(`/bots/${bot.id}`)}
              className="mt-4 w-full py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              View Details →
            </button>
          </div>
        ))}

        {(!bots || bots.length === 0) && (
          <div className="col-span-full text-center py-12 bg-white rounded-xl border border-dashed border-gray-300">
            <Bot className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No bots yet. Create your first trading bot!</p>
          </div>
        )}
      </div>

      {/* The 3 Formulas Modal */}
      {showFormulasModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold">The 3 Formulas from LunarResearcher</h2>
              <p className="text-gray-500 mt-1">The mathematical edge that separates winners from liquidation</p>
            </div>

            <div className="p-6 space-y-6">
              {/* Formula 1: Expected Value */}
              <div className="bg-blue-50 rounded-xl p-6">
                <div className="flex items-center mb-3">
                  <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-3">1</span>
                  <h3 className="text-lg font-bold">Expected Value (EV)</h3>
                </div>
                <div className="bg-white rounded-lg p-4 font-mono text-center mb-3">
                  EV = P(true) × (1 - P(market)) - (1 - P(true)) × P(market)
                </div>
                <p className="text-gray-600">
                  If EV {'>'} 0, the market has priced the event wrong. This is your edge. 
                  The AI calculates this by estimating true probability from whale activity and opportunity signals.
                </p>
              </div>

              {/* Formula 2: Kelly Criterion */}
              <div className="bg-green-50 rounded-xl p-6">
                <div className="flex items-center mb-3">
                  <span className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold mr-3">2</span>
                  <h3 className="text-lg font-bold">Kelly Criterion</h3>
                </div>
                <div className="bg-white rounded-lg p-4 font-mono text-center mb-3">
                  f* = (bp - q) / b
                  <br />
                  <span className="text-sm text-gray-500">where b = (1 - price) / price</span>
                </div>
                <p className="text-gray-600 mb-2">
                  Position sizing formula. Determines optimal bet size based on edge (b) and probability (p).
                </p>
                <p className="text-sm text-gray-500">
                  We use <strong>Quarter-Kelly</strong> (f* × 0.25) for safety — most traders using full Kelly blow up.
                </p>
              </div>

              {/* Formula 3: Sharpe Ratio */}
              <div className="bg-purple-50 rounded-xl p-6">
                <div className="flex items-center mb-3">
                  <span className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold mr-3">3</span>
                  <h3 className="text-lg font-bold">Sharpe Ratio</h3>
                </div>
                <div className="bg-white rounded-lg p-4 font-mono text-center mb-3">
                  SR = (Expected Return - Risk Free Rate) / Standard Deviation
                </div>
                <p className="text-gray-600">
                  Risk-adjusted return metric. Higher Sharpe = better risk-adjusted performance. 
                  Our AI filters for trades with Sharpe {'>'} 1.5 (institutional quality).
                </p>
              </div>

              {/* Bayesian Update */}
              <div className="bg-orange-50 rounded-xl p-6">
                <div className="flex items-center mb-3">
                  <span className="w-8 h-8 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold mr-3">+</span>
                  <h3 className="text-lg font-bold">Bonus: Bayesian Confidence</h3>
                </div>
                <div className="bg-white rounded-lg p-4 font-mono text-center mb-3">
                  P(A|B) = P(B|A) × P(A) / P(B)
                </div>
                <p className="text-gray-600">
                  Updates belief based on new evidence. The AI uses this to combine multiple signals 
                  (whale activity, market data, news) into a confidence score.
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowFormulasModal(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold">Create Trading Bot</h2>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={newBot.name}
                  onChange={(e) => setNewBot({ ...newBot, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="My AI Bot"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mode</label>
                <select
                  value={newBot.mode}
                  onChange={(e) => setNewBot({ ...newBot, mode: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="COPY_TRADE">Copy Trading</option>
                  <option value="AI_DRIVEN">🧠 AI Driven (3 Formulas)</option>
                  <option value="MARKET_MAKING">Market Making</option>
                </select>
                {newBot.mode === 'AI_DRIVEN' && (
                  <p className="mt-1 text-sm text-blue-600">
                    Uses EV, Kelly Criterion, and Sharpe Ratio for automated decisions
                  </p>
                )}
              </div>

              {strategies && strategies.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Strategy (Optional)
                  </label>
                  <select
                    value={newBot.strategyId}
                    onChange={(e) => setNewBot({ ...newBot, strategyId: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="">No strategy (use AI/default logic)</option>
                    {strategies.map((strategy: any) => (
                      <option key={strategy.id} value={strategy.id}>
                        {strategy.name} ({strategy.rules?.length || 0} rules)
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Link a custom strategy to override AI decisions
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Position Size ($)</label>
                <input
                  type="number"
                  value={newBot.maxPositionSize}
                  onChange={(e) => setNewBot({ ...newBot, maxPositionSize: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kelly Fraction</label>
                <input
                  type="number"
                  step="0.05"
                  min="0.05"
                  max="1"
                  value={newBot.kellyFraction}
                  onChange={(e) => setNewBot({ ...newBot, kellyFraction: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
                <p className="mt-1 text-xs text-gray-500">
                  0.25 = Quarter-Kelly (recommended), 0.5 = Half-Kelly (aggressive)
                </p>
              </div>

              {newBot.mode === 'AI_DRIVEN' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Min Sharpe Ratio</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={newBot.minSharpeRatio}
                      onChange={(e) => setNewBot({ ...newBot, minSharpeRatio: parseFloat(e.target.value) })}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Daily Loss ($)</label>
                    <input
                      type="number"
                      value={newBot.maxDailyLoss}
                      onChange={(e) => setNewBot({ ...newBot, maxDailyLoss: parseFloat(e.target.value) })}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Open Positions</label>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={newBot.maxOpenPositions}
                      onChange={(e) => setNewBot({ ...newBot, maxOpenPositions: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={() => createMutation.mutate(newBot)}
                disabled={!newBot.name}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Create Bot
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
